import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Admin() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [activeTab, setActiveTab] = useState('vehicles');
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [newVehicle, setNewVehicle] = useState({
    make: '',
    model: '',
    year: '',
    price: '',
    status: 'AVAILABLE',
    color: '',
    trim: '',
    images: [],
    description: ''
  });

  useEffect(() => {
    if (token) {
      setIsLoggedIn(true);
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    try {
      const [vehiclesRes, customersRes] = await Promise.all([
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/sold-vehicles`)
      ]);
      setVehicles(vehiclesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, loginForm);
      const newToken = response.data.access_token;
      setToken(newToken);
      localStorage.setItem('adminToken', newToken);
      setIsLoggedIn(true);
      fetchData();
    } catch (error) {
      alert('Login failed. Please check your credentials.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
    setIsLoggedIn(false);
  };

  const createVehicle = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/vehicles`, {
        ...newVehicle,
        year: parseInt(newVehicle.year),
        images: newVehicle.images.filter(img => img.trim())
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewVehicle({
        make: '',
        model: '',
        year: '',
        price: '',
        status: 'AVAILABLE',
        color: '',
        trim: '',
        images: [],
        description: ''
      });
      fetchData();
      alert('Vehicle added successfully!');
    } catch (error) {
      alert('Error adding vehicle.');
    }
  };

  const updateVehicle = async (vehicle) => {
    try {
      await axios.put(`${API}/vehicles/${vehicle.id}`, vehicle, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingVehicle(null);
      fetchData();
      alert('Vehicle updated successfully!');
    } catch (error) {
      alert('Error updating vehicle.');
    }
  };

  const deleteVehicle = async (vehicleId) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;
    try {
      await axios.delete(`${API}/vehicles/${vehicleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      alert('Vehicle deleted successfully!');
    } catch (error) {
      alert('Error deleting vehicle.');
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-gray-800 p-8 rounded-lg max-w-md w-full">
          <h1 className="text-2xl font-bold text-white mb-6 text-center">Admin Login</h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-gray-300 mb-2">Username</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded border focus:border-red-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded border focus:border-red-500 focus:outline-none"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded font-semibold"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <nav className="bg-gray-800 p-4">
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-bold text-white">Gemini Cars Import - Admin</h1>
          <button
            onClick={handleLogout}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
          >
            Logout
          </button>
        </div>
      </nav>

      <div className="p-6">
        <div className="mb-6">
          <div className="flex space-x-4">
            <button
              onClick={() => setActiveTab('vehicles')}
              className={`px-4 py-2 rounded ${activeTab === 'vehicles' ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-300'}`}
            >
              Manage Vehicles ({vehicles.length})
            </button>
            <button
              onClick={() => setActiveTab('customers')}
              className={`px-4 py-2 rounded ${activeTab === 'customers' ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-300'}`}
            >
              Happy Customers ({customers.length})
            </button>
          </div>
        </div>

        {activeTab === 'vehicles' && (
          <div className="space-y-6">
            {/* Add New Vehicle Form */}
            <div className="bg-gray-800 p-6 rounded-lg">
              <h2 className="text-xl font-bold text-white mb-4">Add New Vehicle</h2>
              <form onSubmit={createVehicle} className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 mb-2">Make *</label>
                  <input
                    type="text"
                    value={newVehicle.make}
                    onChange={(e) => setNewVehicle({...newVehicle, make: e.target.value})}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded border focus:border-red-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Model *</label>
                  <input
                    type="text"
                    value={newVehicle.model}
                    onChange={(e) => setNewVehicle({...newVehicle, model: e.target.value})}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded border focus:border-red-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Year *</label>
                  <input
                    type="number"
                    value={newVehicle.year}
                    onChange={(e) => setNewVehicle({...newVehicle, year: e.target.value})}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded border focus:border-red-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Price *</label>
                  <input
                    type="text"
                    placeholder="e.g., KYD $12,500"
                    value={newVehicle.price}
                    onChange={(e) => setNewVehicle({...newVehicle, price: e.target.value})}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded border focus:border-red-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Color *</label>
                  <input
                    type="text"
                    value={newVehicle.color}
                    onChange={(e) => setNewVehicle({...newVehicle, color: e.target.value})}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded border focus:border-red-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Status</label>
                  <select
                    value={newVehicle.status}
                    onChange={(e) => setNewVehicle({...newVehicle, status: e.target.value})}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded border focus:border-red-500 focus:outline-none"
                  >
                    <option value="AVAILABLE">AVAILABLE</option>
                    <option value="PENDING ARRIVAL">PENDING ARRIVAL</option>
                    <option value="RESERVED">RESERVED</option>
                    <option value="SOLD">SOLD</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-gray-300 mb-2">Trim</label>
                  <input
                    type="text"
                    value={newVehicle.trim}
                    onChange={(e) => setNewVehicle({...newVehicle, trim: e.target.value})}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded border focus:border-red-500 focus:outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-gray-300 mb-2">Description</label>
                  <textarea
                    value={newVehicle.description}
                    onChange={(e) => setNewVehicle({...newVehicle, description: e.target.value})}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded border focus:border-red-500 focus:outline-none"
                    rows="3"
                  />
                </div>
                <div className="md:col-span-2">
                  <button
                    type="submit"
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded font-semibold"
                  >
                    Add Vehicle
                  </button>
                </div>
              </form>
            </div>

            {/* Vehicles List */}
            <div className="bg-gray-800 p-6 rounded-lg">
              <h2 className="text-xl font-bold text-white mb-4">Current Inventory</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-white">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left p-2">Vehicle</th>
                      <th className="text-left p-2">Price</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehicles.map((vehicle) => (
                      <tr key={vehicle.id} className="border-b border-gray-700">
                        <td className="p-2">
                          <div>
                            <div className="font-semibold">{vehicle.make} {vehicle.model} {vehicle.year}</div>
                            <div className="text-sm text-gray-400">{vehicle.color} {vehicle.trim && `• ${vehicle.trim}`}</div>
                          </div>
                        </td>
                        <td className="p-2 text-red-400">{vehicle.price}</td>
                        <td className="p-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            vehicle.status === 'AVAILABLE' ? 'bg-green-600' :
                            vehicle.status === 'PENDING ARRIVAL' ? 'bg-yellow-600' :
                            vehicle.status === 'RESERVED' ? 'bg-orange-600' : 'bg-red-600'
                          }`}>
                            {vehicle.status}
                          </span>
                        </td>
                        <td className="p-2">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setEditingVehicle(vehicle)}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => deleteVehicle(vehicle.id)}
                              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'customers' && (
          <div className="bg-gray-800 p-6 rounded-lg">
            <h2 className="text-xl font-bold text-white mb-4">Happy Customers</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {customers.map((customer) => (
                <div key={customer.id} className="bg-gray-700 p-4 rounded">
                  <img
                    src={customer.image}
                    alt={customer.name}
                    className="w-16 h-16 rounded-full mx-auto mb-2 object-cover"
                  />
                  <div className="text-center">
                    <h3 className="text-white font-semibold">{customer.name}</h3>
                    <p className="text-gray-300 text-sm">{customer.car} ({customer.year})</p>
                    <p className="text-gray-400 text-xs italic">"{customer.testimonial}"</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Edit Vehicle Modal */}
      {editingVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Edit Vehicle</h3>
              <button
                onClick={() => setEditingVehicle(null)}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 mb-2">Make</label>
                <input
                  type="text"
                  value={editingVehicle.make}
                  onChange={(e) => setEditingVehicle({...editingVehicle, make: e.target.value})}
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded border focus:border-red-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-gray-300 mb-2">Model</label>
                <input
                  type="text"
                  value={editingVehicle.model}
                  onChange={(e) => setEditingVehicle({...editingVehicle, model: e.target.value})}
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded border focus:border-red-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-gray-300 mb-2">Price</label>
                <input
                  type="text"
                  value={editingVehicle.price}
                  onChange={(e) => setEditingVehicle({...editingVehicle, price: e.target.value})}
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded border focus:border-red-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-gray-300 mb-2">Status</label>
                <select
                  value={editingVehicle.status}
                  onChange={(e) => setEditingVehicle({...editingVehicle, status: e.target.value})}
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded border focus:border-red-500 focus:outline-none"
                >
                  <option value="AVAILABLE">AVAILABLE</option>
                  <option value="PENDING ARRIVAL">PENDING ARRIVAL</option>
                  <option value="RESERVED">RESERVED</option>
                  <option value="SOLD">SOLD</option>
                </select>
              </div>
            </div>
            
            <div className="mt-6 flex space-x-4">
              <button
                onClick={() => setEditingVehicle(null)}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => updateVehicle(editingVehicle)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded"
              >
                Update Vehicle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;